import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench,
  AlertTriangle,
  Clock,
  CheckCircle,
  User,
  MapPin,
  Calendar,
  Plus,
  Search,
  Eye,
  FileText,
  Mail,
} from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { PageHeader } from '@/components/admin';
import { Button, Badge, Modal, Card, Input } from '@/components/common';
import { DataTable } from '@/components/ui/data-table';
import { defects as defectsApi } from '@/utils/api';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDefects, useDefectStats } from '@/hooks/useDefects';
import { useProjects } from '@/hooks/useProjects';
import { useUsers } from '@/hooks/useUsers';

// Status configuration
const STATUS_CONFIG = {
  new: { label: 'Neu', color: 'info', icon: FileText },
  scheduled: { label: 'Terminiert', color: 'info', icon: Calendar },
  assigned: { label: 'Zugewiesen', color: 'warning', icon: User },
  accepted: { label: 'Angenommen', color: 'warning', icon: CheckCircle },
  in_progress: { label: 'In Bearbeitung', color: 'warning', icon: Wrench },
  completed: { label: 'Erledigt', color: 'success', icon: CheckCircle },
  signature_refused: { label: 'Unterschrift verweigert', color: 'danger', icon: AlertTriangle },
  needs_followup: { label: 'Nacharbeit', color: 'danger', icon: AlertTriangle },
  reported: { label: 'Freigemeldet', color: 'success', icon: CheckCircle },
};

const PRIORITY_CONFIG = {
  low: { label: 'Niedrig', color: 'success' },
  normal: { label: 'Normal', color: 'info' },
  high: { label: 'Hoch', color: 'warning' },
  urgent: { label: 'Dringend', color: 'danger' },
};

const CATEGORY_OPTIONS = [
  { value: '', label: 'Alle Kategorien' },
  { value: 'Elektro', label: 'Elektro' },
  { value: 'Sanitaer', label: 'Sanitär' },
  { value: 'Heizung', label: 'Heizung' },
  { value: 'Beleuchtung', label: 'Beleuchtung' },
  { value: 'Sicherheit', label: 'Sicherheit' },
  { value: 'Sonstiges', label: 'Sonstiges' },
];

const Maengel = () => {
  const queryClient = useQueryClient();

  // Bugtracker #93: Beim Öffnen der Mängel-Liste die abgelehnten Mängel als gesehen
  // markieren → roter Punkt in der Sidebar verschwindet (Zeile/Badge bleibt sichtbar).
  useEffect(() => {
    defectsApi
      .markRejectionsSeen()
      .then(() => queryClient.invalidateQueries({ queryKey: ['defects', 'stats'] }))
      .catch(() => {});
  }, [queryClient]);

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Modals
  const [createModal, setCreateModal] = useState(false);
  const [newDefect, setNewDefect] = useState({
    project_id: '',
    property_address: '',
    tenant_name: '',
    tenant_phone: '',
    tenant_email: '',
    description: '',
    deadline: '',
    category: 'Elektro',
    priority: 'normal',
  });

  const { user } = useAuth();

  // Build query params from filters
  const defectParams: Record<string, any> = {};
  if (statusFilter) defectParams.status = statusFilter;
  if (priorityFilter) defectParams.priority = priorityFilter;
  if (projectFilter) defectParams.project_id = projectFilter;

  // Data fetching via hooks
  const { data: defectsList = [], isLoading: loading } = useDefects(defectParams);
  const { data: stats } = useDefectStats();
  const { data: projectsRaw = [] } = useProjects({ includeArchived: true });
  const { data: usersRaw = [] } = useUsers({ role: 'all' });

  const projects = projectsRaw as any[];
  const users = (usersRaw as any[]).filter((u: any) => ['mitarbeiter', 'bauleiter', 'admin'].includes(u.role));

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: typeof newDefect) => defectsApi.create(data),
    onSuccess: () => {
      toast.success('Mängelmeldung angelegt');
      setCreateModal(false);
      setNewDefect({
        project_id: '',
        property_address: '',
        tenant_name: '',
        tenant_phone: '',
        tenant_email: '',
        description: '',
        deadline: '',
        category: 'Elektro',
        priority: 'normal',
      });
      queryClient.invalidateQueries({ queryKey: ['defects'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Fehler beim Anlegen');
    },
  });

  // Poll emails mutation
  const pollMutation = useMutation({
    mutationFn: () => defectsApi.pollEmails().then((r) => r.data),
    onSuccess: (data: any) => {
      if (data.totalDefectsCreated > 0) {
        toast.success(`E-Mail-Abruf abgeschlossen. ${data.totalDefectsCreated} neue Mängel gefunden.`);
      } else {
        toast.success('E-Mail-Abruf abgeschlossen. Keine neuen E-Mails gefunden.');
      }
      queryClient.invalidateQueries({ queryKey: ['defects'] });
    },
    onError: (err: any) => {
      console.error('Email polling error:', err);
      toast.error(err.response?.data?.error || 'Fehler beim E-Mail-Abruf');
    },
  });

  const handleCreate = () => {
    if (!newDefect.project_id || !newDefect.description) {
      toast.error('Projekt und Beschreibung sind erforderlich');
      return;
    }
    createMutation.mutate(newDefect);
  };

  const handlePollEmails = () => {
    pollMutation.mutate();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter defects by search term
  const filteredDefects = (defectsList as any[]).filter(d => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      d.property_address?.toLowerCase().includes(search) ||
      d.tenant_name?.toLowerCase().includes(search) ||
      d.title?.toLowerCase().includes(search) ||
      d.description?.toLowerCase().includes(search) ||
      d.project_name?.toLowerCase().includes(search) ||
      d.reference_number?.toLowerCase().includes(search)
    );
  });

  // Status tabs
  const statusTabs = [
    { value: '', label: 'Alle', count: (defectsList as any[]).length },
    { value: 'new', label: 'Neu', count: stats?.by_status?.new || 0 },
    { value: 'assigned', label: 'Zugewiesen', count: (stats?.by_status?.assigned || 0) + (stats?.by_status?.accepted || 0) },
    { value: 'in_progress', label: 'In Bearbeitung', count: stats?.by_status?.in_progress || 0 },
    { value: 'completed', label: 'Erledigt', count: stats?.by_status?.completed || 0 },
    { value: 'reported', label: 'Freigemeldet', count: stats?.by_status?.reported || 0 },
  ];

  type Defect = (typeof filteredDefects)[number];

  const columns: ColumnDef<Defect>[] = [
    {
      accessorKey: 'id',
      header: 'ID',
      cell: ({ row }) => (
        <span className="font-mono text-sm text-gray-500">#{row.original.id}</span>
      ),
    },
    {
      id: 'address',
      header: 'Adresse / Mieter',
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        return (
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="font-medium text-gray-900">
                {d.property_address || 'Keine Adresse'}
              </div>
              {d.tenant_name && (
                <div className="text-sm text-gray-500 flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {d.tenant_name}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'project_name',
      header: 'Projekt',
      cell: ({ row }) => <span className="text-sm">{row.original.project_name || '-'}</span>,
    },
    {
      id: 'mangel',
      header: 'Mangel',
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        return (
          <p className="truncate text-sm max-w-xs" title={d.title || d.description}>
            {d.title || d.description || '-'}
          </p>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priorität',
      cell: ({ row }) => {
        const config = PRIORITY_CONFIG[row.original.priority] || PRIORITY_CONFIG.normal;
        return <Badge variant={config.color}>{config.label}</Badge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const d = row.original;
        const config = STATUS_CONFIG[d.status] || STATUS_CONFIG.new;
        const wasRejected = d.rejected_reason && d.status === 'scheduled';
        return (
          <div className="flex items-center gap-1.5">
            <Badge variant={config.color}>{config.label}</Badge>
            {wasRejected && (
              <Badge variant="danger" title={d.rejected_reason}>Abgelehnt</Badge>
            )}
          </div>
        );
      },
    },
    {
      id: 'termin',
      header: 'Termin',
      enableSorting: false,
      cell: ({ row }) => {
        const d = row.original;
        const isOverdue =
          d.deadline &&
          new Date(d.deadline) < new Date() &&
          !['completed', 'reported', 'signature_refused'].includes(d.status);
        return (
          <div className={`text-sm ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}`}>
            {d.appointment_date ? (
              <div className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDateTime(d.appointment_date)}
              </div>
            ) : d.deadline ? (
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Frist: {formatDate(d.deadline)}
              </div>
            ) : !d.appointment_date && d.appointment_notes === 'Kein Termin nötig' ? (
              <span className="text-green-600 text-xs">✓ Ohne Termin</span>
            ) : (
              '-'
            )}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => (
        <Link to={`/admin/maengel/${row.original.id}`} onClick={(e) => e.stopPropagation()}>
          <Button variant="outline" size="sm" icon={Eye}>
            Details
          </Button>
        </Link>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Mängelmeldungen"
        description="Verwaltung von Mängelmeldungen mit Zuweisung und Freimeldung"
        actions={
          <>
            {user?.role === 'admin' && (
              <Button
                variant="outline"
                icon={Mail}
                onClick={handlePollEmails}
                loading={pollMutation.isPending}
              >
                E-Mails abrufen
              </Button>
            )}
            <Button variant="primary" icon={Plus} onClick={() => setCreateModal(true)}>
              Neuer Mangel
            </Button>
          </>
        }
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-blue-600">{stats?.total || 0}</p>
              <p className="stat-card-label">Gesamt</p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-yellow-600">
                {(stats?.by_status?.new || 0) + (stats?.by_status?.assigned || 0) + (stats?.by_status?.in_progress || 0)}
              </p>
              <p className="stat-card-label">Offen</p>
            </div>
            <div className="p-3 rounded-lg bg-yellow-50">
              <Wrench className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-red-600">{stats?.overdue || 0}</p>
              <p className="stat-card-label">Überfällig</p>
            </div>
            <div className="p-3 rounded-lg bg-red-50">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center justify-between">
            <div>
              <p className="stat-card-value text-green-600">{stats?.by_status?.reported || 0}</p>
              <p className="stat-card-label">Freigemeldet</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <div className="rounded-xl shadow-sm border border-gray-100 bg-white mb-6">
        <div className="flex flex-wrap gap-4 px-4 py-3">
          <div className="relative min-w-[200px] max-w-sm flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Suche..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg bg-white text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div className="flex gap-1 flex-wrap">
            {statusTabs.map(tab => (
              <button
                key={tab.value}
                onClick={() => setStatusFilter(tab.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === tab.value
                    ? 'bg-primary-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                    statusFilter === tab.value
                      ? 'bg-white/20 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
          <select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)} className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Alle Projekte</option>
            {projects.map(p => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)} className="flex h-9 rounded-md border border-input bg-white px-3 py-1 text-sm">
            <option value="">Alle Prioritaeten</option>
            {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
              <option key={value} value={value}>{config.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredDefects}
        emptyText="Keine Mängelmeldungen gefunden"
      />

      {/* Create Modal */}
      <Modal
        isOpen={createModal}
        onClose={() => setCreateModal(false)}
        title="Neue Mängelmeldung"
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setCreateModal(false)}>
              Abbrechen
            </Button>
            <Button variant="primary" onClick={handleCreate} loading={createMutation.isPending}>
              Anlegen
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Projekt *
            </label>
            <select
              value={newDefect.project_id}
              onChange={(e) => setNewDefect({ ...newDefect, project_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              required
            >
              <option value="">Projekt wählen...</option>
              {projects.filter(p => p.status === 'active').map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse / Objekt
            </label>
            <Input
              value={newDefect.property_address}
              onChange={(e) => setNewDefect({ ...newDefect, property_address: e.target.value })}
              placeholder="z.B. Musterstr. 123, 3. OG links"
            />
          </div>

          {/* Tenant */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mieter
              </label>
              <Input
                value={newDefect.tenant_name}
                onChange={(e) => setNewDefect({ ...newDefect, tenant_name: e.target.value })}
                placeholder="Name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Telefon
              </label>
              <Input
                value={newDefect.tenant_phone}
                onChange={(e) => setNewDefect({ ...newDefect, tenant_phone: e.target.value })}
                placeholder="Telefonnummer"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beschreibung *
            </label>
            <textarea
              value={newDefect.description}
              onChange={(e) => setNewDefect({ ...newDefect, description: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              placeholder="Beschreibung des Mangels..."
              required
            />
          </div>

          {/* Category, Priority, Deadline */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Kategorie
              </label>
              <select
                value={newDefect.category}
                onChange={(e) => setNewDefect({ ...newDefect, category: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {CATEGORY_OPTIONS.slice(1).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priorität
              </label>
              <select
                value={newDefect.priority}
                onChange={(e) => setNewDefect({ ...newDefect, priority: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
              >
                {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
                  <option key={value} value={value}>{config.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Frist
              </label>
              <Input
                type="date"
                value={newDefect.deadline}
                onChange={(e) => setNewDefect({ ...newDefect, deadline: e.target.value })}
              />
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Maengel;
